import { useState } from "react";
import { DEFAULT_FILTERS, type Filters, isDefault } from "../lib/filters";

// The filter panel, shared by every browsing view. Extracted from
// `pages/Showtimes.tsx` alongside MovieCard: one saved filter set drives
// Upcoming, Search and Double Features, so one panel has to edit it.

/** 12-hour label for an hour-of-day, for the time-range picker. */
function hourLabel(hour: number): string {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function HourSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (hour: number) => void;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1"
    >
      {Array.from({ length: 24 }, (_, h) => (
        <option key={h} value={h}>
          {hourLabel(h)}
        </option>
      ))}
    </select>
  );
}

function CheckboxFilter({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-300">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-neutral-300"
      />
      {label}
    </label>
  );
}

export function FilterBar({
  filters,
  premiumFormatOptions,
  onChange,
  showFilmFilters = true,
}: {
  filters: Filters;
  premiumFormatOptions: string[];
  onChange: (next: Filters) => void;
  /** The two whole-film filters, leaving soon and Sarah's picks. Hidden on
   *  Double features, where they would do nothing: its pairs carry neither
   *  badges nor her answers. Hiding beats offering a control that silently
   *  has no effect. */
  showFilmFilters?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    onChange({ ...filters, [key]: value });

  const timeRangeOn = filters.startTimeMin !== null && filters.startTimeMax !== null;

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900/40">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="text-sm text-neutral-300 hover:text-neutral-100"
        >
          {open ? "▾" : "▸"} Filters
        </button>
        {!isDefault(filters) && (
          <button
            onClick={() => onChange(DEFAULT_FILTERS)}
            className="text-xs text-neutral-500 underline hover:text-neutral-300"
          >
            Reset
          </button>
        )}
      </div>

      {open && (
        <div className="space-y-3 border-t border-neutral-800 px-3 py-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <CheckboxFilter
              label="Hide seen"
              checked={filters.hideSeen}
              onChange={(v) => set("hideSeen", v)}
            />
            <CheckboxFilter
              label="A-List only"
              checked={filters.alistOnly}
              onChange={(v) => set("alistOnly", v)}
            />
            <CheckboxFilter
              label="Watchlist only"
              checked={filters.watchlistOnly}
              onChange={(v) => set("watchlistOnly", v)}
            />
            <CheckboxFilter
              label="Hide not interested"
              checked={filters.hideNotInterested}
              onChange={(v) => set("hideNotInterested", v)}
            />
            {showFilmFilters && (
              <>
                <CheckboxFilter
                  label="Leaving soon only"
                  checked={filters.closingOnly}
                  onChange={(v) => set("closingOnly", v)}
                />
                <CheckboxFilter
                  label="Sarah wants to see"
                  checked={filters.sarahOnly}
                  onChange={(v) => set("sarahOnly", v)}
                />
              </>
            )}
          </div>

          <div className="space-y-2 border-t border-neutral-800 pt-3">
            <CheckboxFilter
              label="Premium formats only"
              checked={filters.premiumOnly}
              onChange={(v) => set("premiumOnly", v)}
            />
            {filters.premiumOnly && premiumFormatOptions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pl-6">
                {premiumFormatOptions.map((format) => {
                  // No selection means "any premium format", matching the
                  // server -- so with none picked they all read as active.
                  const on =
                    filters.premiumFormats.length === 0 ||
                    filters.premiumFormats.includes(format);
                  return (
                    <button
                      key={format}
                      aria-pressed={on}
                      onClick={() =>
                        set(
                          "premiumFormats",
                          filters.premiumFormats.includes(format)
                            ? filters.premiumFormats.filter((f) => f !== format)
                            : [...filters.premiumFormats, format],
                        )
                      }
                      className={`rounded-full px-2.5 py-1 text-xs ${
                        on
                          ? "bg-neutral-700 text-neutral-100"
                          : "border border-neutral-800 text-neutral-500 hover:border-neutral-600"
                      }`}
                    >
                      {format}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Offered on every view. The server applies a saved range to all
              of them, so hiding the control on a range only hid a filter
              that was still narrowing every date. */}
          <div className="space-y-2 border-t border-neutral-800 pt-3">
            <CheckboxFilter
              label="Start time range"
              checked={timeRangeOn}
              // Turning it on seeds a usable evening range rather than 0-23,
              // which would be a filter that visibly does nothing.
              onChange={(v) =>
                onChange({
                  ...filters,
                  startTimeMin: v ? 17 : null,
                  startTimeMax: v ? 23 : null,
                })
              }
            />
            {timeRangeOn && (
              <div className="flex items-center gap-2 pl-6 text-sm">
                <HourSelect
                  label="Earliest start"
                  value={filters.startTimeMin ?? 0}
                  // Each end carries the other with it rather than crossing it.
                  // An inverted range hides every showtime and reads as a
                  // broken page rather than as a filter doing its job.
                  onChange={(min) =>
                    onChange({
                      ...filters,
                      startTimeMin: min,
                      startTimeMax: Math.max(min, filters.startTimeMax ?? min),
                    })
                  }
                />
                <span className="text-neutral-500">to</span>
                <HourSelect
                  label="Latest start"
                  value={filters.startTimeMax ?? 23}
                  onChange={(max) =>
                    onChange({
                      ...filters,
                      startTimeMin: Math.min(max, filters.startTimeMin ?? max),
                      startTimeMax: max,
                    })
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
